import type { FC } from 'react';
import { Button, Menu, MenuItem, MenuSection, MenuTrigger, Popover, Text, Header } from '@launchpad-ui/components';
import type { IconName } from '@launchpad-ui/icons';
import { Icon } from '@launchpad-ui/icons';

import type { PermissionMode } from '../../AgentInsightsContext';

interface PermissionModePickerProps {
  permissionMode: PermissionMode;
  githubIntegrated: boolean;
  disabled?: boolean;
  /** Icon shown to the left of the trigger label. */
  iconLeft?: IconName;
  onChange: (next: PermissionMode) => void;
}

const READ_PRIMARY = "Read only mode that doesn't affect your code";
const AGENT_PRIMARY =
  'Agent can create dashboards, experiments, and graphs from your data, and (with the GitHub app) author pull requests';
const AGENT_SECONDARY_NO_GH = 'GitHub app required for PR-writing flows';

export const PermissionModePicker: FC<PermissionModePickerProps> = ({
  permissionMode,
  githubIntegrated,
  disabled = false,
  iconLeft,
  onChange,
}) => {
  const buttonLabel = permissionMode === 'read-write' ? 'Agent' : 'Read-only';
  const agentDescription = githubIntegrated ? AGENT_PRIMARY : `${AGENT_PRIMARY}. ${AGENT_SECONDARY_NO_GH}.`;

  return (
    <MenuTrigger>
      <Button variant="default" size="small" isDisabled={disabled}>
        {iconLeft ? <Icon name={iconLeft} size="small" /> : null}
        {buttonLabel}
        <Icon name="chevron-down" size="tiny" />
      </Button>
      <Popover placement="top start">
        <Menu
          aria-label="Select AI mode"
          selectionMode="single"
          selectedKeys={[permissionMode]}
          onAction={(key) => {
            if (key !== 'read' && key !== 'read-write') {
              return;
            }
            if (key !== permissionMode) {
              onChange(key);
            }
          }}
        >
          <MenuSection>
            <Header>
              <Text slot="label">Select AI mode</Text>
            </Header>
            <MenuItem id="read" textValue="Read-only">
              <Text slot="label">
                <Icon name="text-box-search" size="small" /> Read-only
              </Text>
              <Text slot="description">{READ_PRIMARY}</Text>
            </MenuItem>
            <MenuItem id="read-write" textValue="Agent">
              <Text slot="label">
                <Icon name="graduation-cap" size="small" /> Agent
              </Text>
              <Text slot="description">{agentDescription}</Text>
            </MenuItem>
          </MenuSection>
        </Menu>
      </Popover>
    </MenuTrigger>
  );
};
